import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import cloneDeep from 'lodash.clonedeep';
import { SupportedChainId } from '@orionprotocol/sdk';
import { keyValueStorage } from '@orionprotocol/common';
// import { TradeOrder } from '@orionprotocol/trading-terminal/src/types';
import BigNumber from 'bignumber.js';

export type PoolsVerificationState = {
  token: string;
  authInProcess: boolean;
  authHeader?: {
    auth: string;
  };
};
export type BalancesState = {
  wallet: Partial<Record<string, string>>;
  contract: Partial<Record<string, string>>;
  allowance: Partial<Record<string, string>>;
  tradable: Partial<Record<string, string>>;
  locked: {
    local: {
      swapThroughOrionPool: Partial<Record<string, string>>;
      atomic: {
        pending: Partial<Record<string, string>>;
        active: Partial<Record<string, string>>;
      };
    };
    external: {
      aggregator: Partial<Record<string, string>>;
    };
  };
};

interface WalletState {
  walletType?: string;
  address?: string;
  walletChainId?: number;
  isConnecting?: boolean;
  walletConnectWalletName?: string;
}

type UserState = {
  balances: Partial<Record<SupportedChainId, BalancesState>>;
  wallet: WalletState;
  poolsVerification: PoolsVerificationState;
};

export interface ConnectedWallet {
  walletType: string;
  address: string;
  walletChainId?: number;
}

const loadWalletFromLocalStorage = (): ConnectedWallet | undefined => {
  const walletType = keyValueStorage.getItem('walletType');
  if (walletType === undefined) return undefined;
  const address = keyValueStorage.getItem('walletAddress');
  if (address === undefined) return undefined;
  const walletChainId = Number(keyValueStorage.getItem('walletChainId'));

  return { walletType, address, walletChainId };
};
const localStorageWalletInfo = loadWalletFromLocalStorage();

const initialBalance: BalancesState = {
  wallet: {},
  contract: {},
  allowance: {},
  tradable: {},
  locked: {
    local: {
      swapThroughOrionPool: {},
      atomic: {
        pending: {},
        active: {},
      },
    },
    external: {
      aggregator: {},
    },
  },
};

const initialState: UserState = {
  balances: {},
  wallet: {},
  poolsVerification: {
    token: '',
    authInProcess: false,
    authHeader: undefined,
  },
};

if (localStorageWalletInfo) {
  initialState.wallet.address = localStorageWalletInfo.address;
  initialState.wallet.walletType = localStorageWalletInfo.walletType;
  initialState.wallet.walletChainId = localStorageWalletInfo.walletChainId;
}

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    disconnectWallet(state) {
      state.wallet.address = undefined;
      state.wallet.walletType = undefined;
      state.wallet.walletChainId = undefined;

      // TODO Remove this side-effects
      keyValueStorage.removeItem('walletType');
      keyValueStorage.removeItem('walletAddress');
      keyValueStorage.removeItem('walletChainId');
    },
    setWallet(
      state,
      action: PayloadAction<{
        chainId?: number;
        address?: string;
        walletType?: string;
        walletConnectWalletName?: string;
      }>,
    ) {
      const { address, chainId, walletType, walletConnectWalletName } =
        action.payload;
      if (address !== undefined) {
        state.wallet.address = address;
        keyValueStorage.setItem('walletAddress', address);
      }
      if (chainId !== undefined) {
        state.wallet.walletChainId = chainId;
        keyValueStorage.setItem('walletChainId', chainId.toString());
      }

      if (walletType !== undefined) {
        state.wallet.walletType = walletType;
      }

      if (walletConnectWalletName !== undefined) {
        state.wallet.walletConnectWalletName = walletConnectWalletName;
      }

      // TODO Remove this side-effects
      if (state.wallet.walletType !== undefined) {
        keyValueStorage.setItem('walletType', state.wallet.walletType);
      }
    },
    setGuestAccess(state, action: PayloadAction<string>) {
      state.wallet.walletType = 'guest';
      state.wallet.address = action.payload;

      // TODO Remove this side-effects
      keyValueStorage.setItem('walletAddress', action.payload);
      if (state.wallet.walletType)
        keyValueStorage.setItem('walletType', state.wallet.walletType);
    },
    setWalletType(state, action: PayloadAction<string>) {
      state.wallet.walletType = action.payload;
    },
    setIsWalletConnecting(state, action: PayloadAction<boolean>) {
      state.wallet.isConnecting = action.payload;
    },
    setBalances(
      state,
      action: PayloadAction<{
        data: Partial<
          Record<
            string,
            {
              tradable: string;
              reserved: string;
              contract: string;
              wallet: string;
              allowance: string;
            }
          >
        >;
        chainId: SupportedChainId;
      }>,
    ) {
      const { chainId, data } = action.payload;

      if (!(chainId in state.balances))
        state.balances[chainId] = cloneDeep(initialBalance);
      const chainBalance = state.balances[chainId];

      if (chainBalance) {
        Object.entries(data).forEach(([asset, balances]) => {
          if (!balances) return;
          const { tradable, reserved, contract, wallet, allowance } = balances;
          chainBalance.tradable[asset] = tradable;
          chainBalance.wallet[asset] = wallet;
          chainBalance.contract[asset] = contract;
          chainBalance.allowance[asset] = allowance;
          chainBalance.locked.external.aggregator[asset] = reserved;
        });
      }
    },
    clearBalances(state) {
      state.balances = {};
    },
    changeAdminToken(state, action: PayloadAction<string>) {
      state.poolsVerification.token = action.payload;
    },
    changeAdminAuthHeader(state, action: PayloadAction<{ auth: string }>) {
      state.poolsVerification.authHeader = action.payload;
    },
    changeAuthInProcess(state, action: PayloadAction<boolean>) {
      state.poolsVerification.authInProcess = action.payload;
    },
    /*    lockPendingBalance(
          state,
          action: PayloadAction<{
            orders: TradeOrder[];
            chainId: SupportedChainId;
          }>,
        ) {
          const { orders, chainId } = action.payload;
          const chainBalance = state.balances[chainId];
    
          if (chainBalance) {
            chainBalance.locked.local.swapThroughOrionPool = {};
    
            if (orders.length > 0) {
              orders.forEach((order) => {
                const { baseCurrency: lockingAsset, amount, price, type } = order;
    
                if (lockingAsset === null) return;
    
                const lockingAmount =
                  type === 'SELL'
                    ? amount
                    : new BigNumber(amount).multipliedBy(price);
    
                const prevValue =
                  chainBalance.locked.local.swapThroughOrionPool[lockingAsset] ?? 0;
                chainBalance.locked.local.swapThroughOrionPool[lockingAsset] =
                  new BigNumber(prevValue === '' ? 0 : prevValue)
                    .plus(lockingAmount)
                    .toString();
              });
            }
          }
        }, */
  },
});

export const userReducer = userSlice.reducer;
